import React, { useMemo, useRef, useState, useEffect } from "react";
import {
  Box,
  Drawer,
  Typography,
  Select,
  MenuItem,
  InputBase,
  IconButton,
  CircularProgress,
  useTheme,
} from "@mui/material";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import SendRoundedIcon from "@mui/icons-material/SendRounded";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import { useAppSelector } from "redux/hooks";
import { RootState } from "utils/store";
import { RequestFile } from "models/interfaces";
import { MobileSignRequestStyles } from "../MobileSignRequest.styles";
import { askPdf, fetchDocumentSummary } from "../../SignRequestService";
import { convertFileForBackend } from "screens/Shared/CommonHelper";
import {
  apiErrorMessage,
  openErrorNotification,
  handleErrorMessage,
} from "helpers/methods";
import { pressableProps } from "../mobileA11y";

type TabKey = "chat" | "summarize" | "glance";

interface MobileAiAssistantSheetProps {
  open: boolean;
  onClose: () => void;
}

interface ChatMessage {
  role: "user" | "assistant";
  content: string;
}

const TABS: { key: TabKey; label: string }[] = [
  { key: "chat", label: "Chat" },
  { key: "summarize", label: "Document Summarize" },
  { key: "glance", label: "Contract at Glance" },
];

const QUICK_PROMPTS = [
  "Auto-place fields",
  "Summarize this doc",
  "Where does each signer sign?",
];

const formatLabel = (key: string) =>
  key
    .split("_")
    .map((w) => (w ? w.charAt(0).toUpperCase() + w.slice(1) : w))
    .join(" ");

const MobileAiAssistantSheet: React.FC<MobileAiAssistantSheetProps> = ({
  open,
  onClose,
}) => {
  const theme = useTheme();
  const m = theme.palette.mobile;
  const classes = MobileSignRequestStyles(theme);
  const gradient = classes.primaryGradient;

  const { signRequest, selectedFile } = useAppSelector(
    (s: RootState) => s.app,
  );
  const files: RequestFile[] = useMemo(
    () => signRequest.files ?? [],
    [signRequest.files],
  );

  const [tab, setTab] = useState<TabKey>("chat");

  // Chat state
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [input, setInput] = useState("");
  const [isTyping, setIsTyping] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement | null>(null);

  // Summarize / glance state
  const initialDoc =
    selectedFile?.unsignedDoc || files[0]?.unsignedDoc || "";
  const [summarizeDoc, setSummarizeDoc] = useState<string>(initialDoc);
  const [glanceDoc, setGlanceDoc] = useState<string>(initialDoc);
  const [summary, setSummary] = useState<Record<string, any> | null>(null);
  const [glance, setGlance] = useState<Record<string, any> | null>(null);
  const [summaryLoading, setSummaryLoading] = useState(false);
  const [glanceLoading, setGlanceLoading] = useState(false);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, isTyping]);

  const getFileByName = (name: string) =>
    files.find((f) => (f.unsignedDoc || f.unsignedPdf) === name);

  const sendMessage = async (question = input) => {
    const q = question.trim();
    if (!q) return;
    if (files.length === 0) {
      openErrorNotification("Please upload a document first.");
      return;
    }
    setMessages((prev) => [...prev, { role: "user", content: q }]);
    setInput("");
    setIsTyping(true);
    try {
      const payload = {
        files: files.map((f) => ({
          fileName: f.unsignedPdf || f.unsignedDoc,
          requestId: signRequest.id,
        })),
        question: q,
      };
      const res = await askPdf(payload);
      const answer =
        (res?.multiPdfResults || [])
          .map((r: any) => r?.answer)
          .filter(Boolean)
          .join("\n\n") || "I couldn't find an answer for that.";
      setMessages((prev) => [...prev, { role: "assistant", content: answer }]);
    } catch (error) {
      setMessages((prev) => [
        ...prev,
        {
          role: "assistant",
          content: apiErrorMessage(
            error,
            "Sorry, I ran into an error processing that. Please try again.",
          ),
        },
      ]);
    } finally {
      setIsTyping(false);
    }
  };

  const runSummary = async () => {
    const file = getFileByName(summarizeDoc);
    if (!file) {
      openErrorNotification("Please select a document.");
      return;
    }
    try {
      setSummaryLoading(true);
      const res = await fetchDocumentSummary(
        signRequest.id,
        convertFileForBackend(file),
      );
      setSummary(res || null);
    } catch (error: any) {
      handleErrorMessage(error);
    } finally {
      setSummaryLoading(false);
    }
  };

  const runGlance = async () => {
    const file = getFileByName(glanceDoc);
    if (!file) {
      openErrorNotification("Please select a document.");
      return;
    }
    try {
      setGlanceLoading(true);
      const res = await fetchDocumentSummary(
        signRequest.id,
        convertFileForBackend(file),
      );
      // Core sends `contractAtGlance`; the snake_case spelling is kept only as a fallback.
      setGlance(res?.contractAtGlance ?? res?.contract_at_a_glance ?? null);
    } catch (error: any) {
      handleErrorMessage(error);
    } finally {
      setGlanceLoading(false);
    }
  };

  const renderData = (data: any, level = 0): React.ReactNode => {
    if (data === null || data === undefined || data === "") {
      return (
        <Typography
          sx={{ fontSize: "0.8rem", color: m.grey[400], fontStyle: "italic" }}
        >
          Not specified
        </Typography>
      );
    }
    if (Array.isArray(data)) {
      return (
        <Box component="ul" sx={{ pl: 2, m: 0 }}>
          {data.map((item, i) => (
            <Box component="li" key={i} sx={{ mb: 0.5 }}>
              {renderData(item, level + 1)}
            </Box>
          ))}
        </Box>
      );
    }
    if (typeof data === "object") {
      return (
        <Box>
          {Object.entries(data).map(([key, value]) => (
            <Box key={key} sx={{ mb: 1 }}>
              <Typography
                sx={{
                  fontSize: "0.8rem",
                  fontWeight: 700,
                  color: m.base[900],
                }}
              >
                {formatLabel(key)}
              </Typography>
              <Box sx={{ pl: 1 }}>{renderData(value, level + 1)}</Box>
            </Box>
          ))}
        </Box>
      );
    }
    return (
      <Typography
        sx={{ fontSize: "0.8rem", color: m.grey[900], whiteSpace: "pre-wrap" }}
      >
        {String(data)}
      </Typography>
    );
  };

  // The sections are the nested `summary` map, which is what the desktop panel renders. Falling back
  // to the envelope put `documentId` and `terminationDate` on screen as rows of their own.
  const summaryData = useMemo(() => {
    const sections = (summary as Record<string, any> | null)?.summary;
    return sections && typeof sections === "object"
      ? (sections as Record<string, any>)
      : null;
  }, [summary]);

  // What to say when there are no sections: the AI reports a per-document failure as `error` with no
  // `summary`, and its reason is more use than a blank panel. Same line the desktop panel shows.
  const summaryMessage = useMemo(() => {
    if (!summary || summaryData) return "";
    const error = (summary as Record<string, any>).error;
    return typeof error === "string" && error.trim()
      ? error.trim()
      : "No summary data available";
  }, [summary, summaryData]);

  const aiIconBadge = (
    <Box
      sx={{
        width: 34,
        height: 34,
        borderRadius: "10px",
        background: gradient,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        flexShrink: 0,
      }}
    >
      <AutoAwesomeIcon sx={{ fontSize: 18, color: m.surface.white }} />
    </Box>
  );

  const greetingBubble = (
    <Box sx={{ display: "flex", gap: 1.25, mb: 2 }}>
      {aiIconBadge}
      <Box
        sx={{
          bgcolor: m.peach[100],
          borderRadius: "14px",
          p: 1.5,
        }}
      >
        <Typography sx={{ fontSize: "0.875rem", color: m.base[900] }}>
          Hi- I can read your documents and help place fields, explain clauses,
          or route signers. What do you want to do?
        </Typography>
      </Box>
    </Box>
  );

  const docSelect = (value: string, onChange: (v: string) => void) => (
    <Select
      fullWidth
      value={files.some((f) => f.unsignedDoc === value) ? value : ""}
      displayEmpty
      onChange={(e) => onChange(e.target.value as string)}
      IconComponent={KeyboardArrowDownIcon}
      inputProps={{ "aria-label": "Select a document" }}
      renderValue={(v) =>
        v ? (
          (v as string)
        ) : (
          <span style={{ color: m.grey[400] }}>Select a document</span>
        )
      }
      sx={{
        borderRadius: "12px",
        fontSize: "0.9rem",
        "& .MuiOutlinedInput-notchedOutline": {
          borderColor: m.surface[100],
        },
        "&:hover .MuiOutlinedInput-notchedOutline": {
          borderColor: theme.palette.primary.main,
        },
      }}
    >
      {files.map((f, i) => (
        <MenuItem key={i} value={f.unsignedDoc} sx={{ fontSize: "0.875rem" }}>
          {f.unsignedDoc || f.unsignedPdf}
        </MenuItem>
      ))}
    </Select>
  );

  const gradientButton = (
    label: string,
    onClick: () => void,
    loading: boolean,
  ) => (
    <Box
      {...pressableProps(onClick, { label, disabled: loading })}
      sx={{
        mt: 2,
        height: 48,
        borderRadius: "12px",
        background: gradient,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        cursor: loading ? "default" : "pointer",
        opacity: loading ? 0.8 : 1,
      }}
    >
      {loading ? (
        <CircularProgress size={20} sx={{ color: m.surface.white }} />
      ) : (
        <Typography
          sx={{ color: m.surface.white, fontWeight: 700, fontSize: "0.9rem" }}
        >
          {label}
        </Typography>
      )}
    </Box>
  );

  return (
    <Drawer
      anchor="bottom"
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: {
          borderTopLeftRadius: 18,
          borderTopRightRadius: 18,
          height: "88vh",
          display: "flex",
          flexDirection: "column",
        },
        role: "dialog",
        "aria-modal": true,
        "aria-labelledby": "ai-sheet-title",
      }}
    >
      {/* Drag handle */}
      <Box sx={{ display: "flex", justifyContent: "center", pt: 1.25 }}>
        <Box
          aria-hidden="true"
          sx={{ width: 42, height: 4, borderRadius: 2, bgcolor: m.grey[50] }}
        />
      </Box>

      {/* Header */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          px: 2,
          pt: 1.5,
          pb: 1,
        }}
      >
        <Typography
          id="ai-sheet-title"
          component="h2"
          sx={{ fontSize: "1.125rem", fontWeight: 700, color: m.base[900] }}
        >
          Inkfree AI
        </Typography>
        <Typography
          {...pressableProps(onClose, { label: "Close Inkfree AI" })}
          sx={{
            fontSize: "0.9375rem",
            fontWeight: 600,
            color: m.grey[400],
            cursor: "pointer",
          }}
        >
          Cancel
        </Typography>
      </Box>

      {/* Tabs */}
      <Box
        role="tablist"
        aria-label="Inkfree AI tools"
        sx={{
          display: "flex",
          borderBottom: `1px solid ${m.surface[100]}`,
          px: 1,
        }}
      >
        {TABS.map((t) => {
          const active = tab === t.key;
          return (
            <Box
              key={t.key}
              {...pressableProps(() => setTab(t.key), {
                role: "tab",
                selected: active,
                selectedAttr: "aria-selected",
                label: t.label,
              })}
              sx={{
                flex: 1,
                textAlign: "center",
                py: 1.25,
                cursor: "pointer",
                position: "relative",
              }}
            >
              <Typography
                sx={{
                  fontSize: "0.85rem",
                  fontWeight: active ? 700 : 500,
                  color: active ? m.base[900] : m.grey[400],
                }}
              >
                {t.label}
              </Typography>
              {active && (
                <Box
                  sx={{
                    position: "absolute",
                    left: 12,
                    right: 12,
                    bottom: -1,
                    height: 3,
                    borderRadius: 3,
                    background: gradient,
                  }}
                />
              )}
            </Box>
          );
        })}
      </Box>

      {/* Content */}
      <Box sx={{ flex: 1, minHeight: 0, overflowY: "auto", px: 2, py: 2 }}>
        {tab === "chat" && (
          <>
            {greetingBubble}
            {messages.length === 0 && (
              <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, mb: 2 }}>
                {QUICK_PROMPTS.map((p) => (
                  <Box
                    key={p}
                    {...pressableProps(() => sendMessage(p))}
                    sx={{
                      border: `1px solid ${m.surface[100]}`,
                      borderRadius: "10px",
                      px: 1.5,
                      py: 1,
                      cursor: "pointer",
                      fontSize: "0.8125rem",
                      color: m.base[900],
                    }}
                  >
                    {p}
                  </Box>
                ))}
              </Box>
            )}
            <Box role="log" aria-live="polite" aria-relevant="additions">
              {messages.map((msg, i) => (
                <Box
                  key={i}
                  sx={{
                    display: "flex",
                    justifyContent:
                      msg.role === "user" ? "flex-end" : "flex-start",
                    mb: 1.5,
                  }}
                >
                  <Box
                    sx={{
                      maxWidth: "82%",
                      bgcolor:
                        msg.role === "user" ? m.blue[50] : m.peach[100],
                      borderRadius: "14px",
                      p: 1.5,
                    }}
                  >
                    <Typography
                      sx={{
                        fontSize: "0.875rem",
                        color: m.base[900],
                        whiteSpace: "pre-wrap",
                      }}
                    >
                      <Box component="span" sx={{ position: "absolute", width: 1, height: 1, overflow: "hidden", clip: "rect(0 0 0 0)" }}>
                        {msg.role === "user" ? "You said: " : "Assistant said: "}
                      </Box>
                      {msg.content}
                    </Typography>
                  </Box>
                </Box>
              ))}
              {isTyping && (
                <Box sx={{ display: "flex", gap: 1.25, mb: 1.5 }}>
                  {aiIconBadge}
                  <Box
                    sx={{ bgcolor: m.peach[100], borderRadius: "14px", p: 1.5 }}
                  >
                    <CircularProgress
                      size={16}
                      sx={{ color: m.orange[500] }}
                      aria-label="Assistant is typing"
                    />
                  </Box>
                </Box>
              )}
            </Box>
            <div ref={messagesEndRef} />
          </>
        )}

        {tab === "summarize" && (
          <>
            {greetingBubble}
            <Typography
              sx={{
                fontSize: "0.75rem",
                fontWeight: 700,
                color: m.grey[400],
                letterSpacing: "0.04em",
                mb: 1,
              }}
            >
              SELECT DOCUMENT
            </Typography>
            {docSelect(summarizeDoc, setSummarizeDoc)}
            {gradientButton("Document Summarize", runSummary, summaryLoading)}
            {summaryData && (
              <Box
                sx={{
                  mt: 2,
                  border: `1px solid ${m.surface[100]}`,
                  borderRadius: "14px",
                  p: 1.5,
                }}
              >
                {renderData(summaryData)}
              </Box>
            )}
            {summaryMessage && (
              <Box
                sx={{
                  mt: 2,
                  border: `1px solid ${m.surface[100]}`,
                  borderRadius: "14px",
                  p: 2,
                }}
              >
                <Typography
                  sx={{
                    fontSize: "0.8rem",
                    color: m.grey[400],
                    textAlign: "center",
                  }}
                >
                  {summaryMessage}
                </Typography>
              </Box>
            )}
          </>
        )}

        {tab === "glance" && (
          <>
            {greetingBubble}
            <Typography
              sx={{
                fontSize: "0.75rem",
                fontWeight: 700,
                color: m.grey[400],
                letterSpacing: "0.04em",
                mb: 1,
              }}
            >
              SELECT DOCUMENT
            </Typography>
            {docSelect(glanceDoc, setGlanceDoc)}
            {gradientButton("Contract at Glance", runGlance, glanceLoading)}
            {glance && (
              <Box
                sx={{
                  mt: 2,
                  border: `1px solid ${m.surface[100]}`,
                  borderRadius: "14px",
                  p: 1.5,
                }}
              >
                {renderData(glance)}
              </Box>
            )}
          </>
        )}
      </Box>

      {/* Chat input footer */}
      {tab === "chat" && (
        <Box
          sx={{
            borderTop: `1px solid ${m.surface[100]}`,
            p: 1.5,
            display: "flex",
            alignItems: "center",
            gap: 1,
          }}
        >
          <InputBase
            fullWidth
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") sendMessage();
            }}
            placeholder="Ask AI about your documents..."
            inputProps={{ "aria-label": "Ask AI about your documents" }}
            sx={{
              border: `1px solid ${m.surface[100]}`,
              borderRadius: "12px",
              px: 1.5,
              py: 1,
              fontSize: "0.875rem",
            }}
          />
          <IconButton
            onClick={() => sendMessage()}
            disabled={isTyping || !input.trim()}
            aria-label="Send message"
            sx={{
              width: 44,
              height: 44,
              background: gradient,
              flexShrink: 0,
              "&:hover": { background: gradient },
              "&.Mui-disabled": { background: gradient, opacity: 0.5 },
            }}
          >
            <SendRoundedIcon sx={{ fontSize: 20, color: m.surface.white }} />
          </IconButton>
        </Box>
      )}
    </Drawer>
  );
};

export default MobileAiAssistantSheet;
